/**
 * FeaturedBanner — reusable featured-item banner components.
 *
 * Exports:
 *   SingleFeaturedBanner  — single item at a time, image-left + detail-right layout
 *                           with 3-card swipe track (prev|current|next) on mobile,
 *                           fade-based cross-fading on desktop with side-arrow hover.
 *                           Accepts `items`, `accentColor`, `accentDark`, `ctaLabel`,
 *                           `sectionLabel`, `onCta`, `dataSelectTarget`, and optional
 *                           `imageIntervalMs` / `intervalMs`.
 *
 *   GridFeaturedBanner    — multi-card grid (xs:1 swipe / md:2 slots / lg+:3 slots
 *                           with fade cycling). Accepts `list`, `onVisit`,
 *                           `transformImageUrl`, `sectionLabel`, `accentColor`,
 *                           `accentDark`, `ctaLabel`, and `dataSelectTarget`.
 *
 *   SingleFeaturedBannerSkeleton / GridFeaturedBannerSkeleton — loading
 *                           placeholders that mirror their banner's layout.
 *
 * All components are whitelabel-ready: every user-visible string, accent
 * colour, navigation callback, and `data-select-target` value is passed in as
 * a prop so the banners can be reused for events, organizations, or any other
 * featured-item section without modification.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Typography, Button, Chip, Divider, Skeleton, useMediaQuery } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import IconButton from "@mui/material/IconButton";
import { isImageCached, markImageLoaded } from "@/utils/imageCache";

// ─────────────────────────────────────────────────────────────────────────────
// Shared constants
// ─────────────────────────────────────────────────────────────────────────────
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const SWIPE_THRESHOLD = 50; // px to register a swipe
const SINGLE_CYCLE_MS = 13000; // default auto-advance interval

// ─────────────────────────────────────────────────────────────────────────────
// Utility helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the display description for a featured organisation card.
 *
 * When the tagline (`orgDescripTitle`) is absent or is the generic
 * "About Us" placeholder, fall back to the full `description` text
 * trimmed to 135 characters so the card stays legible.
 */
export function featuredOrgDescription(orgDescripTitle, description) {
  const title = (orgDescripTitle ?? "").trim();
  const useTitle = title && title.toLowerCase() !== "about us";
  if (useTitle) return title;
  const desc = (description ?? "").trim();
  return desc.length > 135 ? `${desc.slice(0, 135).trimEnd()}…` : desc;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A row of small tag chips shown on a featured card (e.g. a professional's
 * expertise / languages / location). Groups with no tags are skipped.
 */

/** Compact chip rows rendered on featured cards; hidden when nothing to show. */
const FeaturedTagGroups = ({
  groups
}) => {
  const visible = (groups ?? []).filter(g => g.tags.length > 0);
  if (visible.length === 0) return null;
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      overflow: "hidden",
      flexShrink: 0
    },
    children: visible.map((group, i) => {
      const max = group.max ?? 3;
      const shown = group.tags.slice(0, max);
      const extra = group.tags.length - shown.length;
      const dark = group.colorDark ?? group.color;
      return /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          justifyContent: "center"
        },
        children: [shown.map(tag => /*#__PURE__*/_jsx(Chip, {
          label: tag,
          size: "small",
          sx: group.variant === "filled" ? {
            bgcolor: alpha(group.color, 0.12),
            color: dark,
            fontWeight: 600
          } : {
            bgcolor: "transparent",
            border: `1px solid ${alpha(group.color, 0.5)}`,
            color: dark
          }
        }, tag)), extra > 0 && /*#__PURE__*/_jsx(Chip, {
          label: `+${extra}`,
          size: "small",
          sx: {
            bgcolor: "transparent",
            border: `1px dashed ${alpha(group.color, 0.4)}`,
            color: "text.secondary"
          }
        })]
      }, i);
    })
  });
};
// ─────────────────────────────────────────────────────────────────────────────
// SingleFeaturedBannerSkeleton — shown while items are loading
// ─────────────────────────────────────────────────────────────────────────────

export const SingleFeaturedBannerSkeleton = ({
  accentColor,
  hideImageMobile = false
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  return /*#__PURE__*/_jsx(SFBWrapper, {
    accentColor: accentColor,
    children: !isMd ?
    /*#__PURE__*/
    /* ── Mobile skeleton ── */
    _jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Box, {
        sx: {
          backgroundColor: accentColor,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },
        children: /*#__PURE__*/_jsx(Skeleton, {
          variant: "rectangular",
          width: 120,
          height: 28,
          sx: {
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.25)"
          }
        })
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          flexDirection: "column",
          minHeight: hideImageMobile ? 140 : 420,
          height: hideImageMobile ? "auto" : 420,
          overflow: "hidden"
        },
        children: [!hideImageMobile && /*#__PURE__*/_jsx(Skeleton, {
          variant: "rectangular",
          sx: {
            height: 200,
            flexShrink: 0
          }
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 2,
            gap: 1.5
          },
          children: [/*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "70%",
            height: 28
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "90%"
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "80%"
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "55%"
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              mt: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1
            },
            children: [/*#__PURE__*/_jsx(Skeleton, {
              variant: "rounded",
              width: 80,
              height: 24
            }), /*#__PURE__*/_jsx(Skeleton, {
              variant: "rounded",
              width: 100,
              height: 32
            })]
          })]
        })]
      })]
    }) :
    /*#__PURE__*/
    /* ── Desktop skeleton ── */
    _jsxs(Box, {
      sx: {
        display: "grid",
        gridTemplateColumns: "minmax(280px, 40%) 1fr",
        height: 380
      },
      children: [/*#__PURE__*/_jsx(Skeleton, {
        variant: "rectangular",
        sx: {
          height: "100%"
        }
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          flexDirection: "column"
        },
        children: [/*#__PURE__*/_jsx(Box, {
          sx: {
            backgroundColor: accentColor,
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          },
          children: /*#__PURE__*/_jsx(Skeleton, {
            variant: "rectangular",
            width: 140,
            height: 28,
            sx: {
              borderRadius: 1,
              bgcolor: "rgba(255,255,255,0.25)"
            }
          })
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            p: {
              xs: 3,
              md: 4
            },
            display: "flex",
            flexDirection: "column",
            flex: 1,
            gap: 2
          },
          children: [/*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "65%",
            height: 36
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "90%"
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "85%"
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "60%"
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              mt: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1
            },
            children: [/*#__PURE__*/_jsx(Skeleton, {
              variant: "rounded",
              width: 80,
              height: 24
            }), /*#__PURE__*/_jsx(Skeleton, {
              variant: "rounded",
              width: 120,
              height: 36
            })]
          })]
        })]
      })]
    })
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// SingleFeaturedBanner — module-level mobile card (stable component type)
// Defining this outside the parent component prevents React from unmounting
// the cards on every touchmove render, which would break swipe tracking.
// ─────────────────────────────────────────────────────────────────────────────

const SFBMobileCard = ({
  item,
  accentColor,
  accentDark,
  ctaLabel,
  hideImage,
  onCta
}) => {
  const [imgLoaded, setImgLoaded] = useState(() => isImageCached(item.image));
  const [imgError, setImgError] = useState(false);
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "flex",
      flexDirection: "column",
      minHeight: hideImage ? 140 : 420,
      height: hideImage ? "auto" : 420,
      overflow: "hidden"
    },
    children: [!hideImage && /*#__PURE__*/_jsxs(Box, {
      sx: {
        position: "relative",
        height: 200,
        flexShrink: 0,
        overflow: "hidden",
        // Match the "Most relevant to you" panel treatment,
        // color-matched per page (theme accent).
        backgroundColor: alpha(accentColor, 0.3),
        border: `1px solid ${accentColor}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      children: [!imgLoaded && !imgError && /*#__PURE__*/_jsx(Skeleton, {
        variant: "rectangular",
        width: "100%",
        height: 200
      }), !imgError && /*#__PURE__*/_jsx(ContainedImageOnWhite, {
        src: item.image,
        alt: item.name,
        visible: imgLoaded,
        onLoad: () => {
          markImageLoaded(item.image);
          setImgLoaded(true);
        },
        onError: () => setImgError(true)
      }), imgError && /*#__PURE__*/_jsx(Box, {
        sx: {
          width: "100%",
          height: "100%",
          bgcolor: alpha(accentColor, 0.3)
        }
      })]
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        p: 2,
        gap: 1,
        overflow: "hidden"
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        fontWeight: 700,
        sx: {
          color: accentColor,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word"
        },
        children: item.name
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        color: "text.secondary",
        sx: {
          // No flex-grow/shrink: inside the fixed-height card
          // a flexed box can end up a fractional line tall and
          // crop text mid-line. The clamp alone sets the
          // height, so truncation always lands on a line
          // boundary with an ellipsis.
          flexShrink: 0,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word"
        },
        children: item.description
      }), /*#__PURE__*/_jsx(FeaturedTagGroups, {
        groups: item.tagGroups
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          mt: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1
        },
        children: [item.price !== undefined && /*#__PURE__*/_jsx(Chip, {
          label: item.price === 0 ? "FREE" : `${item.price} ${item.currency || "USD"}`,
          size: "small",
          sx: {
            fontWeight: 700
          }
        }), /*#__PURE__*/_jsx(Button, {
          variant: "contained",
          size: "small",
          onClick: () => onCta(item),
          sx: {
            alignSelf: "center",
            backgroundColor: accentColor,
            "&:hover": {
              backgroundColor: accentDark
            }
          },
          children: ctaLabel
        })]
      })]
    })]
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// SingleFeaturedBanner
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An image scaled to fill its box's width OR height (objectFit contain),
 * sitting on a WHITE plate sized exactly to the rendered image. The plate
 * sits between the accent-tinted panel and the image, so a transparent
 * logo reads on white instead of picking up the panel tint — while the
 * letterbox bars around it stay tinted. Opaque photos cover the plate
 * completely, so they are unaffected.
 */
const ContainedImageOnWhite = ({
  src,
  alt,
  visible = true,
  onLoad,
  onError
}) => {
  const boxRef = useRef(null);
  const naturalRef = useRef(null);
  const [plate, setPlate] = useState(null);
  const compute = useCallback(() => {
    const box = boxRef.current;
    const nat = naturalRef.current;
    if (!box || !nat || !nat.w || !nat.h) return;
    const cw = box.clientWidth;
    const ch = box.clientHeight;
    if (!cw || !ch) return;
    const scale = Math.min(cw / nat.w, ch / nat.h);
    setPlate({
      w: Math.max(1, Math.round(nat.w * scale)),
      h: Math.max(1, Math.round(nat.h * scale))
    });
  }, []);
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => compute());
    ro.observe(box);
    return () => ro.disconnect();
  }, [compute]);

  // New src → stale plate no longer applies until the load remeasures.
  useEffect(() => {
    naturalRef.current = null;
    setPlate(null);
  }, [src]);
  return /*#__PURE__*/_jsx(Box, {
    ref: boxRef,
    sx: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    children: /*#__PURE__*/_jsx(Box, {
      sx: {
        bgcolor: "common.white",
        display: "flex",
        ...(plate ? {
          width: plate.w,
          height: plate.h
        } : {
          width: "100%",
          height: "100%"
        }),
        visibility: visible ? "visible" : "hidden"
      },
      children: /*#__PURE__*/_jsx(Box, {
        component: "img",
        src: src,
        alt: alt,
        onLoad: e => {
          const img = e.currentTarget;
          naturalRef.current = {
            w: img.naturalWidth,
            h: img.naturalHeight
          };
          compute();
          onLoad?.();
        },
        onError: onError,
        sx: {
          width: "100%",
          height: "100%",
          objectFit: "contain"
        }
      })
    })
  });
};
const SFBWrapper = styled(Box, {
  shouldForwardProp: p => p !== "accentColor"
})(() => ({
  position: "relative",
  // No perimeter border: with contain-on-white images the frame around
  // the image side read as stray lines. The accent border now wraps the
  // TEXT column only (see "Right: details"); the image floats.
  borderRadius: 0,
  backgroundColor: "#ffffff",
  overflow: "hidden",
  marginBottom: 0,
  "&:hover .sfb-side-arrow": {
    opacity: 1
  }
}));

/**
 * SingleFeaturedBanner
 *
 * Desktop: image on the left, coloured title band + details on the right.
 *   • Side arrows appear on hover when there are 2+ items.
 *   • Content fades out/in during item transitions.
 *   • The left image supports its own slideshow when multiple images are supplied.
 *
 * Mobile (xs): flash-free 3-card track (prev | current | next) in an
 *   overflow-hidden container. Swipe left/right to cycle; threshold is 50 px.
 *   With exactly 2 items the same item fills both prev AND next slots so
 *   swiping either direction works correctly. With 1 item swiping is disabled.
 */
export const SingleFeaturedBanner = ({
  items,
  accentColor,
  accentDark,
  ctaLabel,
  sectionLabel,
  onCta,
  dataSelectTarget,
  intervalMs = SINGLE_CYCLE_MS,
  imageIntervalMs = 12000,
  hideImageMobile = false
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  // ── Desktop state ─────────────────────────────────────────────────────────
  const [index, setIndex] = useState(0);
  const [imgIndex, setImgIndex] = useState(0);
  const [contentOpacity, setContentOpacity] = useState(1);
  const [imageOpacity, setImageOpacity] = useState(1);
  const [imageHovered, setImageHovered] = useState(false);
  const indexRef = useRef(index);
  const pendingIndex = useRef(null);
  const count = items.length;
  const current = count > 0 ? items[index] : null;
  const currentImages = useMemo(() => current?.images?.length ? current.images : current ? [current.image] : [],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [current?.id]);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);
  const animateTo = useCallback(next => {
    if (next === indexRef.current) return;
    pendingIndex.current = next;
    setContentOpacity(0);
    setImageOpacity(0);
    setTimeout(() => {
      setIndex(next);
      pendingIndex.current = null;
      setContentOpacity(1);
      setImageOpacity(1);
    }, 350);
  }, []);
  const goPrevItem = useCallback(() => {
    if (count <= 1) return;
    animateTo((indexRef.current - 1 + count) % count);
  }, [animateTo, count]);
  const goNextItem = useCallback(() => {
    if (count <= 1) return;
    animateTo((indexRef.current + 1) % count);
  }, [animateTo, count]);

  // Reset image index when item changes
  useEffect(() => {
    setImgIndex(0);
  }, [index]);

  // Image slideshow
  useEffect(() => {
    if (currentImages.length <= 1) return;
    const id = setInterval(() => setImgIndex(i => (i + 1) % currentImages.length), imageIntervalMs);
    return () => clearInterval(id);
  }, [currentImages.length, imageIntervalMs]);

  // Desktop item auto-advance
  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => {
      animateTo((indexRef.current + 1) % count);
    }, intervalMs);
    return () => clearInterval(id);
  }, [animateTo, count, intervalMs]);

  // ── Mobile state ──────────────────────────────────────────────────────────
  const [mobileIndex, setMobileIndex] = useState(0);
  const [trackTransform, setTrackTransform] = useState("translateX(-33.333%)");
  const [trackTransition, setTrackTransition] = useState(false);
  const [mobileTimerKey, setMobileTimerKey] = useState(0);
  const mobileIndexRef = useRef(0);
  const busyRef = useRef(false);
  const touchStartX = useRef(null);
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  // Reset only when the SET of items actually changes, not when a caller
  // hands us a fresh-but-equal array — otherwise a parent re-render mid
  // cross-fade snaps the animation back and flickers.
  const itemsSignature = useMemo(() => items.map(i => i.id).join("|"), [items]);

  // Reset when items list changes
  useEffect(() => {
    setMobileIndex(0);
    mobileIndexRef.current = 0;
    setTrackTransform("translateX(-33.333%)");
    setTrackTransition(false);
    busyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsSignature]);
  const slideToNext = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    const N = itemsRef.current.length;
    const next = (mobileIndexRef.current + 1) % N;
    setTrackTransition(true);
    setTrackTransform("translateX(-66.666%)");
    setTimeout(() => {
      setMobileIndex(next);
      mobileIndexRef.current = next;
      setTrackTransition(false);
      setTrackTransform("translateX(-33.333%)");
      busyRef.current = false;
    }, 300);
  }, []);
  const slideToPrev = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    const N = itemsRef.current.length;
    const prev = (mobileIndexRef.current - 1 + N) % N;
    setTrackTransition(true);
    setTrackTransform("translateX(0%)");
    setTimeout(() => {
      setMobileIndex(prev);
      mobileIndexRef.current = prev;
      setTrackTransition(false);
      setTrackTransform("translateX(-33.333%)");
      busyRef.current = false;
    }, 300);
  }, []);

  // Mobile auto-advance — restarts when mobileTimerKey changes
  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => {
      if (itemsRef.current.length <= 1) return;
      slideToNext();
    }, intervalMs);
    return () => clearInterval(id);
  }, [slideToNext, mobileTimerKey, count, intervalMs]);
  const handleTouchStart = useCallback(e => {
    if (busyRef.current || count <= 1) return;
    touchStartX.current = e.touches[0].clientX;
  }, [count]);
  const handleTouchMove = useCallback(e => {
    if (touchStartX.current === null || busyRef.current) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    setTrackTransition(false);
    setTrackTransform(`translateX(calc(-33.333% + ${delta}px))`);
  }, []);
  const handleTouchEnd = useCallback(e => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (itemsRef.current.length <= 1) {
      setTrackTransition(true);
      setTrackTransform("translateX(-33.333%)");
      setTimeout(() => setTrackTransition(false), 300);
      return;
    }
    if (delta < -SWIPE_THRESHOLD) {
      setMobileTimerKey(k => k + 1);
      slideToNext();
    } else if (delta > SWIPE_THRESHOLD) {
      setMobileTimerKey(k => k + 1);
      slideToPrev();
    } else {
      setTrackTransition(true);
      setTrackTransform("translateX(-33.333%)");
      setTimeout(() => setTrackTransition(false), 300);
    }
  }, [slideToNext, slideToPrev]);
  if (count === 0 || !current) return null;

  // ── Mobile render (xs) ────────────────────────────────────────────────────
  if (!isMd) {
    const N = count;
    // For 2 items: slot the same single other item into both prev AND next
    // so swiping in either direction works (infinite feel with 2 items).
    const prevItem = N > 1 ? items[(mobileIndex - 1 + N) % N] : items[0];
    const currItem = items[mobileIndex];
    const nextItem = N > 1 ? items[(mobileIndex + 1) % N] : items[0];
    return /*#__PURE__*/_jsxs(SFBWrapper, {
      accentColor: accentColor,
      "data-select-target": dataSelectTarget,
      children: [/*#__PURE__*/_jsx(Box, {
        sx: {
          backgroundColor: accentColor,
          color: "#fff",
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },
        children: /*#__PURE__*/_jsxs(Typography, {
          variant: "h5",
          sx: {
            m: 0,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            textAlign: "center"
          },
          children: [sectionLabel, count > 1 ? "s" : ""]
        })
      }), /*#__PURE__*/_jsx(Box, {
        sx: {
          overflow: "hidden",
          position: "relative"
        },
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        children: /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            width: "300%",
            transform: trackTransform,
            transition: trackTransition ? "transform 300ms ease-in-out" : "none",
            willChange: "transform"
          },
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              width: "33.333%",
              flexShrink: 0
            },
            children: /*#__PURE__*/_jsx(SFBMobileCard, {
              item: prevItem,
              accentColor: accentColor,
              accentDark: accentDark,
              ctaLabel: ctaLabel,
              hideImage: hideImageMobile,
              onCta: onCta
            })
          }), /*#__PURE__*/_jsx(Box, {
            sx: {
              width: "33.333%",
              flexShrink: 0
            },
            children: /*#__PURE__*/_jsx(SFBMobileCard, {
              item: currItem,
              accentColor: accentColor,
              accentDark: accentDark,
              ctaLabel: ctaLabel,
              hideImage: hideImageMobile,
              onCta: onCta
            })
          }), /*#__PURE__*/_jsx(Box, {
            sx: {
              width: "33.333%",
              flexShrink: 0
            },
            children: /*#__PURE__*/_jsx(SFBMobileCard, {
              item: nextItem,
              accentColor: accentColor,
              accentDark: accentDark,
              ctaLabel: ctaLabel,
              hideImage: hideImageMobile,
              onCta: onCta
            })
          })]
        })
      })]
    });
  }

  // ── Desktop render (md+) ──────────────────────────────────────────────────
  const goPrevImage = () => setImgIndex(i => currentImages.length === 0 ? 0 : (i - 1 + currentImages.length) % currentImages.length);
  const goNextImage = () => setImgIndex(i => currentImages.length === 0 ? 0 : (i + 1) % currentImages.length);
  return /*#__PURE__*/_jsxs(SFBWrapper, {
    accentColor: accentColor,
    "data-select-target": dataSelectTarget,
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(280px, 40%) 1fr"
        },
        height: {
          xs: "auto",
          md: 380
        },
        minHeight: {
          xs: 420,
          md: 380
        }
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        onMouseEnter: () => setImageHovered(true),
        onMouseLeave: () => setImageHovered(false),
        sx: {
          position: "relative",
          minHeight: {
            xs: 200,
            md: "100%"
          },
          height: {
            md: "100%"
          },
          overflow: "hidden",
          opacity: imageOpacity,
          transition: "opacity 350ms ease-in-out",
          backgroundColor: alpha(accentColor, 0.3),
          border: `1px solid ${accentColor}`
        },
        children: [currentImages.map((src, i) => /*#__PURE__*/_jsx(Box, {
          sx: {
            position: "absolute",
            inset: 0,
            opacity: i === imgIndex ? 1 : 0,
            transition: "opacity 600ms ease-in-out",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          },
          children: /*#__PURE__*/_jsx(ContainedImageOnWhite, {
            src: src,
            alt: current.name,
            onLoad: () => markImageLoaded(src)
          })
        }, `${current.id}-img-${i}`)), imageHovered && currentImages.length > 1 && /*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 8,
            display: "flex",
            justifyContent: "center",
            gap: 1,
            zIndex: 2
          },
          children: [/*#__PURE__*/_jsx(IconButton, {
            size: "small",
            onClick: e => {
              e.stopPropagation();
              goPrevImage();
            },
            "aria-label": "Previous image",
            sx: {
              bgcolor: "rgba(0,0,0,0.45)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.65)"
              }
            },
            children: /*#__PURE__*/_jsx(ArrowBackIosNewIcon, {
              fontSize: "small"
            })
          }), /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            onClick: e => {
              e.stopPropagation();
              goNextImage();
            },
            "aria-label": "Next image",
            sx: {
              bgcolor: "rgba(0,0,0,0.45)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.65)"
              }
            },
            children: /*#__PURE__*/_jsx(ArrowForwardIosIcon, {
              fontSize: "small"
            })
          })]
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          flexDirection: "column",
          border: `2px solid ${accentColor}`
        },
        children: [/*#__PURE__*/_jsx(Box, {
          sx: {
            backgroundColor: accentColor,
            color: "#fff",
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          },
          children: /*#__PURE__*/_jsxs(Typography, {
            variant: "h5",
            sx: {
              m: 0,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              textAlign: "center"
            },
            children: [sectionLabel, count > 1 ? "s" : ""]
          })
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            p: {
              xs: 3,
              md: 4
            },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
            gap: 2,
            overflow: "hidden",
            opacity: contentOpacity,
            transition: "opacity 350ms ease-in-out"
          },
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "h5",
            fontWeight: 700,
            sx: {
              color: accentColor,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            },
            children: current.name
          }), /*#__PURE__*/_jsx(Typography, {
            variant: "body1",
            color: "text.secondary",
            sx: {
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              flex: 1
            },
            children: current.description
          }), /*#__PURE__*/_jsx(FeaturedTagGroups, {
            groups: current.tagGroups
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              mt: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1
            },
            children: [current.price !== undefined && /*#__PURE__*/_jsx(Chip, {
              label: current.price === 0 ? "FREE" : `${current.price} ${current.currency || "USD"}`,
              size: "small",
              sx: {
                fontWeight: 700
              }
            }), /*#__PURE__*/_jsx(Button, {
              variant: "contained",
              size: "small",
              onClick: () => onCta(current),
              sx: {
                alignSelf: "center",
                backgroundColor: accentColor,
                "&:hover": {
                  backgroundColor: accentDark
                }
              },
              children: ctaLabel
            })]
          })]
        })]
      })]
    }), count > 1 && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Box, {
        className: "sfb-side-arrow",
        onClick: goPrevItem,
        role: "button",
        "aria-label": `Previous ${sectionLabel.toLowerCase()}`,
        sx: {
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "10%",
          minWidth: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          pl: 1,
          cursor: "pointer",
          opacity: 0,
          transition: "opacity .2s ease-in-out",
          zIndex: 3
        },
        children: /*#__PURE__*/_jsx(IconButton, {
          size: "medium",
          sx: {
            bgcolor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.7)"
            }
          },
          children: /*#__PURE__*/_jsx(ArrowBackIosNewIcon, {})
        })
      }), /*#__PURE__*/_jsx(Box, {
        className: "sfb-side-arrow",
        onClick: goNextItem,
        role: "button",
        "aria-label": `Next ${sectionLabel.toLowerCase()}`,
        sx: {
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "10%",
          minWidth: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          pr: 1,
          cursor: "pointer",
          opacity: 0,
          transition: "opacity .2s ease-in-out",
          zIndex: 3
        },
        children: /*#__PURE__*/_jsx(IconButton, {
          size: "medium",
          sx: {
            bgcolor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.7)"
            }
          },
          children: /*#__PURE__*/_jsx(ArrowForwardIosIcon, {})
        })
      })]
    })]
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// GridFeaturedBanner
// ─────────────────────────────────────────────────────────────────────────────

const GRID_CYCLE_MS = 13000;
const GRID_FADE_MS = 400;
// Inner card
const GridCard = ({
  item,
  onVisit,
  tx,
  accentColor,
  accentDark,
  ctaLabel
}) => {
  const VisitBtn = styled(Button)(() => ({
    marginTop: 0,
    backgroundColor: accentColor,
    color: "#fff",
    "&:hover": {
      backgroundColor: accentDark
    }
  }));
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      p: 3,
      minWidth: 0,
      height: 300,
      overflow: "hidden"
    },
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 2
      },
      children: [item.logoUrl && /*#__PURE__*/_jsx(Box, {
        component: "img",
        src: tx(item.logoUrl),
        alt: `${item.name} logo`,
        sx: {
          width: {
            xs: 48,
            md: 64
          },
          height: {
            xs: 48,
            md: 64
          },
          // Square logo with a subtle 3px radius to match the
          // org cards in ResultsSection (OrganizationLogo).
          borderRadius: 3,
          // Contain on white so non-square logos aren't
          // cropped and transparent ones don't sit on grey.
          objectFit: "contain",
          bgcolor: "common.white",
          flexShrink: 0
        }
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        sx: {
          fontWeight: 700,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
          lineHeight: 1.25
        },
        children: item.name
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "body1",
      sx: {
        // No flex-grow/shrink (see mobile card note): the clamp
        // sets the height so truncation is always a clean
        // ellipsis, never a mid-line crop.
        flexShrink: 0,
        display: "-webkit-box",
        // Tag rows need vertical room inside the fixed-height card.
        WebkitLineClamp: item.tagGroups?.length ? 2 : 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: "text.secondary"
      },
      children: item.description || ""
    }), /*#__PURE__*/_jsx(FeaturedTagGroups, {
      groups: item.tagGroups
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        mt: "auto"
      },
      children: /*#__PURE__*/_jsx(VisitBtn, {
        variant: "contained",
        size: "medium",
        onClick: () => onVisit(item),
        sx: {
          alignSelf: "center"
        },
        children: ctaLabel(item.name)
      })
    })]
  });
};
const GFBWrapper = styled(Box, {
  shouldForwardProp: p => p !== "accentColor"
})(({
  theme,
  accentColor
}) => ({
  marginBottom: theme.spacing(4),
  padding: 0,
  border: `2px solid ${accentColor}`,
  borderRadius: 0,
  backgroundColor: theme.palette.background.default,
  overflow: "hidden"
}));

/**
 * GridFeaturedBanner
 *
 * xs:  1 card visible with 3-card swipe track (flash-free).
 * md:  2 slots side-by-side; extras fade in to replace slots on a timer.
 * lg+: 3 slots; same slot-fade cycling.
 *
 * All user-visible strings, colours, and callbacks are passed as props.
 */
export const GridFeaturedBanner = ({
  list,
  onVisit,
  transformImageUrl,
  sectionLabel,
  ctaLabel = name => `Visit ${name}`,
  accentColor,
  accentDark,
  dataSelectTarget
}) => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const tx = transformImageUrl ?? (u => u);
  const slotCount = isLg ? 3 : isMd ? 2 : 1;
  const slotCountRef = useRef(slotCount);
  useEffect(() => {
    slotCountRef.current = slotCount;
  }, [slotCount]);
  const [slotIndices, setSlotIndices] = useState([0, 1, 2]);
  const [slotOpacity, setSlotOpacity] = useState([1, 1, 1]);
  const nextCounterRef = useRef(3);
  const swapSlotRef = useRef(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [trackTransform, setTrackTransform] = useState("translateX(-33.333%)");
  const [trackTransition, setTrackTransition] = useState(false);
  const [mobileTimerKey, setMobileTimerKey] = useState(0);
  const mobileIndexRef = useRef(0);
  const busyRef = useRef(false);
  const touchStartX = useRef(null);
  const listRef = useRef(list);
  useEffect(() => {
    listRef.current = list;
  }, [list]);
  // Signature so a fresh-but-equal array from a parent re-render doesn't
  // reset the slot fades mid-animation (flicker source).
  const listSignature = useMemo(() => list.map(i => i.id ?? i.slug ?? i.name).join("|"), [list]);
  useEffect(() => {
    setSlotIndices([0, 1, 2]);
    setSlotOpacity([1, 1, 1]);
    nextCounterRef.current = 3;
    swapSlotRef.current = 0;
    setMobileIndex(0);
    mobileIndexRef.current = 0;
    setTrackTransform("translateX(-33.333%)");
    setTrackTransition(false);
    busyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSignature]);
  const slideToNext = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    const next = (mobileIndexRef.current + 1) % listRef.current.length;
    setTrackTransition(true);
    setTrackTransform("translateX(-66.666%)");
    setTimeout(() => {
      setMobileIndex(next);
      mobileIndexRef.current = next;
      setTrackTransition(false);
      setTrackTransform("translateX(-33.333%)");
      busyRef.current = false;
    }, 300);
  }, []);
  const slideToPrev = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    const prev = (mobileIndexRef.current - 1 + listRef.current.length) % listRef.current.length;
    setTrackTransition(true);
    setTrackTransform("translateX(0%)");
    setTimeout(() => {
      setMobileIndex(prev);
      mobileIndexRef.current = prev;
      setTrackTransition(false);
      setTrackTransform("translateX(-33.333%)");
      busyRef.current = false;
    }, 300);
  }, []);

  // Slot-fade cycling (md / lg)
  useEffect(() => {
    const id = setInterval(() => {
      const sc = slotCountRef.current;
      if (sc < 2 || listRef.current.length <= sc) return;
      const slot = swapSlotRef.current % sc;
      const nextOrgIdx = nextCounterRef.current % listRef.current.length;
      setSlotOpacity(prev => {
        const copy = prev.slice();
        copy[slot] = 0;
        return copy;
      });
      setTimeout(() => {
        setSlotIndices(prev => {
          const copy = prev.slice();
          copy[slot] = nextOrgIdx;
          return copy;
        });
        setSlotOpacity(prev => {
          const copy = prev.slice();
          copy[slot] = 1;
          return copy;
        });
        nextCounterRef.current++;
        swapSlotRef.current = (slot + 1) % sc;
      }, GRID_FADE_MS);
    }, GRID_CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  // Mobile auto-advance
  useEffect(() => {
    const id = setInterval(() => {
      if (slotCountRef.current !== 1) return;
      if (listRef.current.length <= 1) return;
      slideToNext();
    }, GRID_CYCLE_MS);
    return () => clearInterval(id);
  }, [slideToNext, mobileTimerKey]);
  const handleTouchStart = useCallback(e => {
    if (busyRef.current) return;
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchMove = useCallback(e => {
    if (touchStartX.current === null || busyRef.current) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    setTrackTransition(false);
    setTrackTransform(`translateX(calc(-33.333% + ${delta}px))`);
  }, []);
  const handleTouchEnd = useCallback(e => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (listRef.current.length <= 1) {
      setTrackTransition(true);
      setTrackTransform("translateX(-33.333%)");
      setTimeout(() => setTrackTransition(false), 300);
      return;
    }
    if (delta < -SWIPE_THRESHOLD) {
      setMobileTimerKey(k => k + 1);
      slideToNext();
    } else if (delta > SWIPE_THRESHOLD) {
      setMobileTimerKey(k => k + 1);
      slideToPrev();
    } else {
      setTrackTransition(true);
      setTrackTransform("translateX(-33.333%)");
      setTimeout(() => setTrackTransition(false), 300);
    }
  }, [slideToNext, slideToPrev]);
  const TitleBand = /*#__PURE__*/_jsx(Box, {
    sx: {
      backgroundColor: accentColor,
      color: "#fff",
      px: 2,
      py: 1.25,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    children: /*#__PURE__*/_jsxs(Typography, {
      variant: "h5",
      sx: {
        m: 0,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        textAlign: "center"
      },
      children: [sectionLabel, list.length > 1 ? "s" : ""]
    })
  });
  if (!isMd) {
    const N = list.length;
    const prevItem = list[(mobileIndex - 1 + N) % N];
    const currItem = list[mobileIndex];
    const nextItem = list[(mobileIndex + 1) % N];
    return /*#__PURE__*/_jsxs(GFBWrapper, {
      accentColor: accentColor,
      "data-select-target": dataSelectTarget,
      children: [TitleBand, /*#__PURE__*/_jsx(Box, {
        sx: {
          overflow: "hidden",
          position: "relative"
        },
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        children: /*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            width: "300%",
            transform: trackTransform,
            transition: trackTransition ? "transform 300ms ease-in-out" : "none",
            willChange: "transform"
          },
          children: [prevItem, currItem, nextItem].map((item, i) => /*#__PURE__*/_jsx(Box, {
            sx: {
              width: "33.333%",
              flexShrink: 0
            },
            children: /*#__PURE__*/_jsx(GridCard, {
              item: item,
              onVisit: onVisit,
              tx: tx,
              accentColor: accentColor,
              accentDark: accentDark,
              ctaLabel: ctaLabel
            })
          }, i))
        })
      }), N > 1 && /*#__PURE__*/_jsx(Box, {
        sx: {
          display: "flex",
          justifyContent: "center",
          gap: 1,
          py: 1.5
        },
        children: list.map((_, i) => /*#__PURE__*/_jsx(Box, {
          onClick: () => {
            if (i === mobileIndex || busyRef.current) return;
            setMobileIndex(i);
            mobileIndexRef.current = i;
            setTrackTransition(false);
            setTrackTransform("translateX(-33.333%)");
            setMobileTimerKey(k => k + 1);
          },
          sx: {
            width: 10,
            height: 10,
            borderRadius: "50%",
            border: `2px solid ${accentColor}`,
            backgroundColor: i === mobileIndex ? accentColor : "transparent",
            cursor: "pointer",
            transition: "background-color 0.3s"
          }
        }, i))
      })]
    });
  }
  const visibleSlots = [0, 1, 2].slice(0, slotCount);
  return /*#__PURE__*/_jsxs(GFBWrapper, {
    accentColor: accentColor,
    "data-select-target": dataSelectTarget,
    children: [TitleBand, /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch"
      },
      children: visibleSlots.map(slot => /*#__PURE__*/_jsxs(React.Fragment, {
        children: [slot > 0 && /*#__PURE__*/_jsx(Divider, {
          orientation: "vertical",
          flexItem: true,
          sx: {
            borderColor: accentColor,
            borderRightWidth: 2
          }
        }), /*#__PURE__*/_jsx(Box, {
          sx: {
            flex: 1,
            minWidth: 0,
            opacity: list.length > slotCount ? slotOpacity[slot] : 1,
            transition: `opacity ${GRID_FADE_MS}ms ease-in-out`
          },
          children: /*#__PURE__*/_jsx(GridCard, {
            item: list[slotIndices[slot] % list.length],
            onVisit: onVisit,
            tx: tx,
            accentColor: accentColor,
            accentDark: accentDark,
            ctaLabel: ctaLabel
          })
        })]
      }, slot))
    })]
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// GridFeaturedBannerSkeleton — shown while items are loading
// ─────────────────────────────────────────────────────────────────────────────

export const GridFeaturedBannerSkeleton = ({
  accentColor,
  dataSelectTarget
}) => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const slotCount = isLg ? 3 : isMd ? 2 : 1;
  const slots = Array.from({
    length: slotCount
  });
  return /*#__PURE__*/_jsxs(GFBWrapper, {
    accentColor: accentColor,
    "data-select-target": dataSelectTarget,
    children: [/*#__PURE__*/_jsx(Box, {
      sx: {
        backgroundColor: accentColor,
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      children: /*#__PURE__*/_jsx(Skeleton, {
        variant: "rectangular",
        width: 180,
        height: 28,
        sx: {
          borderRadius: 1,
          bgcolor: "rgba(255,255,255,0.25)"
        }
      })
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch"
      },
      children: slots.map((_, i) => /*#__PURE__*/_jsxs(React.Fragment, {
        children: [i > 0 && /*#__PURE__*/_jsx(Divider, {
          orientation: "vertical",
          flexItem: true,
          sx: {
            borderColor: accentColor,
            borderRightWidth: 2
          }
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            flex: 1,
            minWidth: 0,
            p: 3,
            height: 300,
            display: "flex",
            flexDirection: "column",
            gap: 1.5
          },
          children: [/*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 2
            },
            children: [/*#__PURE__*/_jsx(Skeleton, {
              variant: "circular",
              width: 64,
              height: 64
            }), /*#__PURE__*/_jsx(Skeleton, {
              variant: "text",
              width: "60%",
              height: 28
            })]
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "95%"
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "85%"
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            width: "70%"
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              mt: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75
            },
            children: [/*#__PURE__*/_jsx(Skeleton, {
              variant: "text",
              width: 100
            }), /*#__PURE__*/_jsx(Skeleton, {
              variant: "rounded",
              width: 140,
              height: 36
            })]
          })]
        })]
      }, i))
    })]
  });
};